import { floorOverlayApi } from '@/api/config'
import { setupApi } from '@/api/config-setup'
import areaApi from '@/api/config/areaApi'
import { floorApi } from '@/api/operations'
import Select from '@/components/form-control/Select'
import GoogleMap, { restrictionDefault } from '@/components/google-map'
import GoogleMap2 from '@/components/google-map/GoogleMap2'
import {
  CloseSmallIcon,
  CurrentLocationIcon,
  LocalPinIcon,
} from '@/components/icons'
import { KEY_GOOGLE_MAP_TYPE } from '@/constant/constants'
import {
  BtnGroupFloor,
  BtnGroupZoom,
  MapToolBox,
  MapTypeButtonGroup,
  MapViewOverlay,
  MapViewOverlayOptions,
} from '@/components/google-map/components'
import {
  MapViewCondition,
  MapViewIcon,
} from '@/components/google-map/components/MapIcons'
import { NotifyService } from '@/helpers'
import { DATA_PAGINATION, OptionItem, SetupStation } from '@/types'
import { AreaParentType, SubAreaParentType } from '@/types/areaZone'
import { Floor } from '@/types/floor'
import { OverlapDetailByFloor, TypeFloorOverlay } from '@/types/siteOverlap'
import { MARKER_TYPE } from '@/types/marker'
import {
  formatPosition,
  getCustomMarkerByType,
  getPinningMarkerSVG,
} from '@/ultils'
import { Box, Button, Stack, Tooltip } from '@mui/material'
import { memo, useEffect, useMemo, useRef, useState } from 'react'
import classes from './styles.module.scss'
import useTranslation from '@/hooks/useTranslation'
import { debounce } from 'lodash'
import RenderIf from '@/components/common/RenderIf'
import CoordinateInput from '@/components/common/CoordinateInput'

enum OPTION_KEY {
  SITE,
  LOCATION,
  AREA,
  SUBAREA,
}

export interface ChangePositionData {
  siteId: number
  siteName: string
  locationId: number
  locationName: string
  floorId: number
  grsCoordinatedLatitude: number
  grsCoordinatedLongitude: number
  areaId: number
  areaName?: string | null
  subAreaId: number
  subAreaName?: string | null
}

export interface StationData {
  id?: number | null
  siteId: number | null
  siteName: string | null
  locationId: number | null
  locationName: string | null
  floorId: number | null
  grsCoordinatedLatitude: number | null
  grsCoordinatedLongitude: number | null
  areaId: number | null
  areaName: string | null
  subAreaId: number | null
  subAreaName: string | null
  markertType?: MARKER_TYPE
}
interface Props {
  interfaceId: number
  station?: StationData
  singleMode?: boolean
  mapOnMap?: boolean
  onClose?: () => void
  onDone: (data: ChangePositionData) => void
  onMarkerClick?: (station: number, markertType?: MARKER_TYPE) => void
  tab?: string
  allStations?: SetupStation[]
  allFloorOverlays?: OverlapDetailByFloor[]
  allowChangePositionCoordinate?: boolean
  isFilterByFloor?: boolean
  isCheckMarkertType?: boolean
}

export const MapPositionStation = memo(
  ({
    interfaceId,
    singleMode,
    mapOnMap,
    tab = '0',
    station,
    onClose,
    onDone,
    onMarkerClick,
    allStations,
    allFloorOverlays,
    allowChangePositionCoordinate,
    isFilterByFloor,
    isCheckMarkertType,
  }: Props) => {
    const { t } = useTranslation()
    const map = useRef<google.maps.Map | null>(null)
    const [siteActive, setSiteActive] = useState<OptionItem>()
    const [locationActive, setLocationActive] = useState<OptionItem>()
    const [areaActive, setAreaActive] = useState<OptionItem>()
    const [subAreaActive, setSubAreaActive] = useState<OptionItem>()

    const [floors, setFloors] = useState<Floor[]>([])
    const [activeFloor, setActiveFloor] = useState<Floor>()
    const overlayDraw = useRef<MapViewOverlay>()
    const markerDraw = useRef<google.maps.Marker>()
    const markersRef = useRef<google.maps.Marker[]>([])

    const [locationFloors, setLocationFloors] =
      useState<Record<number, Floor[]>>()

    const [markerPosition, setMarkerPosition] =
      useState<google.maps.LatLng | null>()
    const [mapType, setMapType] = useState<google.maps.MapTypeId>(
      (localStorage.getItem(KEY_GOOGLE_MAP_TYPE) as google.maps.MapTypeId) ||
        google.maps.MapTypeId.ROADMAP,
    )

    const mapOutput = (value: google.maps.Map) => {
      map.current = value
      map.current.setZoom(17)
    }

    const onZoomIn = () => {
      const zoom = map.current?.getZoom() ?? 0
      if (zoom) {
        map.current?.setZoom(zoom + 1)
      }
    }

    const onZoomOut = () => {
      const zoom = map.current?.getZoom() ?? 0
      if (zoom) {
        map.current?.setZoom(zoom - 1)
      }
    }

    const existingStationLatLong = useMemo(
      () =>
        allStations?.map((item) => ({
          stationId: item.id,
          stationName: item.controller,
          latitude: item.grsCoordinatedLatitude,
          longitude: item.grsCoordinatedLongitude,
          markertType: item.markertType,
          floorId: item.floorId,
        })) || [],
      [allStations],
    )

    // Quick marker at fixed lat/lng for debugging
    useEffect(() => {
      markersRef.current.forEach((m) => m.setMap(null))
      markersRef.current = []

      existingStationLatLong?.forEach((item) => {
        if (station) {
          let flag = item.stationId === station.id
          if (isCheckMarkertType) {
            flag = !!flag && item.markertType === station.markertType
          }
          if (flag) return
        }

        if (isFilterByFloor) {
          if (!activeFloor) return
          if (activeFloor && activeFloor?.id !== item.floorId) return
        }

        const pos = new google.maps.LatLng(item.latitude, item.longitude)
        const marker = new google.maps.Marker({
          position: pos,
          map: map.current!,
          // icon: getRefMarkerSVG(), // or omit for default pin
          icon: getCustomMarkerByType(item.markertType), // or omit for default pin
        })
        if (onMarkerClick) {
          marker.addListener('click', () => {
            onMarkerClick(item.stationId, item.markertType)
          })
        }
        markersRef.current.push(marker)
      })
    }, [existingStationLatLong, station, activeFloor])

    const getOptions =
      (key: OPTION_KEY, id?: number) =>
      async (PageIndex?: number, PageSize?: number, Keyword?: string) => {
        let data: DATA_PAGINATION | undefined
        switch (key) {
          case OPTION_KEY.SITE:
            data = await setupApi
              .getSites(interfaceId, {
                PageIndex,
                PageSize: PageSize || 20,
                Keyword,
              })
              .then(({ data }) => ({
                ...data,
                data: data.data.map((it) => ({ id: it.id, name: it.name })),
              }))
              .catch((e) => {
                NotifyService.error(e)
                return undefined
              })
            break
          case OPTION_KEY.LOCATION:
            if (!siteActive && !id) data = undefined
            else {
              data = await setupApi
                .getLocations(siteActive?.id || id, {
                  PageSize: 0,
                  Keyword,
                  // RequiredOverlay: true,
                })
                .then(({ data }) => ({
                  ...data,
                  data: data.data.map((it) => ({ id: it.id, name: it.name })),
                }))
                .catch((e) => {
                  NotifyService.error(e)
                  return undefined
                })
            }
            break
          case OPTION_KEY.AREA:
            data = await areaApi
              .getAreaParent(interfaceId)
              .then((res) => {
                const raws = res.data as AreaParentType[]
                return {
                  data: raws,
                  page: 1,
                  size: raws.length,
                  totalCount: raws.length,
                  totalPage: raws.length,
                }
              })
              .catch((e) => {
                NotifyService.error(e)
                return undefined
              })
            break
          case OPTION_KEY.SUBAREA:
            data = await areaApi
              .getSubAreaParent(interfaceId, areaActive?.id || 0)
              .then((res) => {
                const raws = res.data as SubAreaParentType[]
                return {
                  data: raws,
                  page: 1,
                  size: raws.length,
                  totalCount: raws.length,
                  totalPage: raws.length,
                }
              })
              .catch((e) => {
                NotifyService.error(e)
                return undefined
              })
            break
        }
        return data
      }

    const drawOverlay = (
      data: OverlapDetailByFloor,
      options?: MapViewOverlayOptions,
    ) => {
      const swBound = new google.maps.LatLng(
        data.bottomLeftLatitude,
        data.bottomLeftLongitude,
      )
      const neBound = new google.maps.LatLng(
        data.topRightLatitude,
        data.topRightLongitude,
      )
      const overlay: MapViewOverlay = new MapViewOverlay(
        swBound,
        neBound,
        data.imageUrl,
        data.rotate,
        map.current as any,
        options,
      )
      return overlay
    }

    const clearOverlay = () => {
      if (!overlayDraw.current) return
      overlayDraw.current.clear()
      overlayDraw.current = undefined
      map.current?.setOptions({
        minZoom: null,
        restriction: restrictionDefault,
      })
    }

    const clearMarker = () => {
      if (!markerDraw.current) return
      markerDraw.current.setMap(null)
      markerDraw.current = undefined
    }

    const createActionMarker = (position: google.maps.LatLng) => {
      const marker = new google.maps.Marker({
        map: map.current,
        zIndex: 600,
        icon: getPinningMarkerSVG(),
        position: position,
        draggable: true,
      })
      setMarkerPosition(position)
      google.maps.event.addListener(marker, 'dragstart', () => {
        let positionNew = marker.getPosition()
        marker.set('dragstartPosition', positionNew)
      })
      google.maps.event.addListener(marker, 'dragend', () => {
        let positionNew = marker.getPosition() as google.maps.LatLng
        // if (overlayBounds && !overlayBounds.contains(positionNew)) {
        //   console.log('llll')
        //   positionNew = overlayBounds.getCenter()
        //   marker.setPosition(marker.get('dragstartPosition') || positionNew)
        // }
        setMarkerPosition(positionNew)
      })
      return marker
    }

    const _onDone = () => {
      const position = markerDraw.current?.getPosition()
      if (!siteActive) {
        NotifyService.error('Site is required')
        return
      }
      if (!locationActive) {
        NotifyService.error('Location is required')
        return
      }
      if (!activeFloor) {
        NotifyService.error(
          'At the moment, there is no overlay at this location',
        )
        return
      }

      if (!areaActive) {
        NotifyService.error('Areas is required')
        return
      }
      if (!subAreaActive) {
        NotifyService.error('Sub-Areas is required')
        return
      }
      if (!overlayDraw.current) {
        NotifyService.error('Overlay is required')
        return
      }
      if (!position) {
        NotifyService.error('Can not change this position')
        return
      }

      onDone({
        // FIXME: double check why use subAreaActive, areaActive, because I see only use first item when submit. => overwrite data when submit
        siteId: siteActive.id,
        siteName: siteActive.name as string,
        locationId: locationActive.id,
        locationName: locationActive.name as string,
        floorId: activeFloor.id,
        grsCoordinatedLatitude: position.lat(),
        grsCoordinatedLongitude: position.lng(),
        subAreaId: subAreaActive.id,
        subAreaName: subAreaActive.name,
        areaId: areaActive.id,
        areaName: areaActive.name,
      })
    }

    const onCurrentLocation = () => {
      if (!map.current) return
      if (overlayDraw.current) {
        const bounds = overlayDraw.current.getBounds()
        map.current.fitBounds(bounds)
        map.current.setCenter(bounds.getCenter())
      } else if (markerDraw.current) {
        const position = markerDraw.current.getPosition()
        if (position) {
          map.current.setCenter(position)
        }
      }
    }

    const onGetFloors = async (locationId?: number) => {
      let _floor: Floor[] = []

      if (locationId) {
        const floors = locationFloors ?? {}
        const savedFloor = floors[locationId]
        // console.log(savedFloor)
        if (!savedFloor) {
          _floor = await floorApi
            .getByLocationId(`${locationId}`, { requiredOverylay: 1 })
            .then(({ data }) => data)
            .catch((err) => {
              NotifyService.error(err)
              return [] as Floor[]
            })
          floors[locationId] = _floor
          setLocationFloors(floors)
        } else {
          _floor = savedFloor
        }
      }

      setFloors(_floor)
      return _floor
    }

    const onGetOverLay = async (
      floorId?: number,
      checkMarkerPosition?: boolean,
      fitToBound = true,
    ) => {
      let _overlay: OverlapDetailByFloor | undefined
      if (floorId) {
        if (allFloorOverlays && allFloorOverlays?.length > 0) {
          _overlay = allFloorOverlays.filter(
            (item) =>
              item.floorId === floorId &&
              item.typeId === TypeFloorOverlay.OverlayWithAllAnnotations,
          )[0]
        }

        if (!_overlay) {
          const overlays = await floorOverlayApi
            .getListByFloorId(floorId)
            .then(({ data }) => data.data)
            .catch((err) => {
              NotifyService.error(err)
              return [] as OverlapDetailByFloor[]
            })
          _overlay = overlays.find(
            (it) => it.typeId === TypeFloorOverlay.OverlayWithAllAnnotations,
          )
        }
      }
      clearOverlay()
      if (_overlay) {
        const _overlayDraw = drawOverlay(_overlay)
        const bounds = _overlayDraw.getBounds()
        overlayDraw.current = _overlayDraw

        if (fitToBound) map.current?.fitBounds(bounds)

        if (checkMarkerPosition && markerDraw.current) {
          const markerPosition = markerDraw.current.getPosition()
          if (markerPosition && !bounds.contains(markerPosition)) {
            markerDraw.current.setPosition(bounds.getCenter())
          }
        }
      }
    }

    const onChangeSite = (val: OptionItem) => {
      setSiteActive(val)
      onChangeLocation()
    }

    const onChangeLocation = async (value?: OptionItem) => {
      setLocationActive(value)
      const _floors = await onGetFloors(value?.id)
      setFloors(_floors)
      const _activeFloor = _floors.find((it) => it.isDefault)
      setActiveFloor(_activeFloor)
      await onGetOverLay(_activeFloor?.id, true)
    }

    const onChangeArea = (val: OptionItem) => {
      setAreaActive(val)
    }

    const onChangeSubArea = (val: OptionItem) => {
      setSubAreaActive(val)
    }

    const onChangeFloor = (item: Floor) => {
      setActiveFloor(item)
      onGetOverLay(item.id, true)
    }

    useEffect(() => {
      if (!!station) {
        // const fetchAreaData = async () => {
        //   await getOptions(OPTION_KEY.AREA)
        //   onChangeArea({
        //     id: station.areaId,
        //     name: station.areaName,
        //   })
        // }
        // const fetchData = async () => {
        //   await getOptions(OPTION_KEY.SUBAREA)
        //   onChangeSubArea({
        //     id: station.subAreaId,
        //     name: station.subAreaName,
        //   })
        // }
        // fetchAreaData()
        // fetchData()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [station])

    useEffect(() => {
      if (!!areaActive && station === undefined) {
        const fetchData = async () => {
          const subAreas = await getOptions(OPTION_KEY.SUBAREA, areaActive.id)()
          if (subAreas?.data?.length) {
            onChangeSubArea(subAreas.data[0])
          }
        }
        fetchData()
      }
      if (!!areaActive) {
        const fetchData = async () => {
          const subAreas = await getOptions(OPTION_KEY.SUBAREA, areaActive.id)()
          if (subAreas?.data?.length && station?.areaId !== areaActive.id) {
            onChangeSubArea(subAreas.data[0])
          } else if (station && station?.areaId === areaActive.id) {
            onChangeSubArea({
              id: station.subAreaId,
              name: station.subAreaName,
            })
          }
        }
        fetchData()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [areaActive])

    useEffect(() => {
      clearMarker()
      if (station) {
        const initData = async () => {
          if (!map.current) return
          const _site = station?.siteId
            ? { id: station.siteId, name: station.siteName }
            : undefined
          const _location =
            station?.locationId && _site
              ? { id: station.locationId, name: station.locationName }
              : undefined
          if (_location) {
            setSiteActive(_site)
            setLocationActive(_location)
            let _activeFloor: Floor | undefined
            const _floors = await onGetFloors(_location?.id)
            if (station.floorId) {
              _activeFloor = _floors.find((item) => item.id === station.floorId)
            } else {
              _activeFloor = _floors.find((it) => it.isDefault)
            }
            setActiveFloor(_activeFloor)
            const hasCoordinates =
              station.grsCoordinatedLatitude && station.grsCoordinatedLongitude
            await onGetOverLay(_activeFloor?.id, true, !hasCoordinates)
          }
          const _areaActive = station.areaId
            ? { id: station.areaId, name: station.areaName }
            : undefined
          const _subAreaActive = station.subAreaId
            ? { id: station.subAreaId, name: station.subAreaName }
            : undefined
          if (_areaActive) setAreaActive(_areaActive)
          if (_subAreaActive) onChangeSubArea(_subAreaActive)

          let position = map.current?.getCenter() as google.maps.LatLng
          if (
            station.grsCoordinatedLatitude &&
            station.grsCoordinatedLongitude
          ) {
            position = new google.maps.LatLng(
              station.grsCoordinatedLatitude,
              station.grsCoordinatedLongitude,
            )
            // const overlayBounds = overlayDraw.current?.getBounds()
            // if (overlayBounds && !overlayBounds?.contains(position)) {
            //   position = overlayBounds.getCenter()
            //   console.log('center')
            // }
          }
          const marker = createActionMarker(position)
          markerDraw.current = marker
          map.current?.panTo(position)
          if (!overlayDraw.current) map.current.setZoom(17)
        }
        initData()
      } else {
        if (singleMode && map.current) {
          let position = map.current?.getCenter() as google.maps.LatLng
          const marker = createActionMarker(position)
          markerDraw.current = marker
          map.current?.panTo(position)
          if (!overlayDraw.current) map.current.setZoom(17)
        }

        if (!singleMode && !locationActive) {
          const getDefault = async () => {
            const sites = await getOptions(OPTION_KEY.SITE)()
            const areas = await getOptions(OPTION_KEY.AREA)()

            if (sites?.data?.length) {
              const locations = await getOptions(
                OPTION_KEY.LOCATION,
                sites.data[0].id,
              )()
              if (locations?.data?.length) {
                setSiteActive(sites.data[0])
                onChangeLocation(locations.data[0])
              }
            }
            if (areas?.data?.length) {
              const subAreas = await getOptions(
                OPTION_KEY.SUBAREA,
                areas.data[0].id,
              )()
              if (subAreas?.data?.length) {
                setAreaActive(areas.data[0])
                onChangeSubArea(subAreas.data[0])
              }
            }
          }
          getDefault()
        }
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [station])

    const onChangeMapType = (type: google.maps.MapTypeId) => {
      setMapType(type)
      localStorage.setItem(KEY_GOOGLE_MAP_TYPE, type)
    }

    useEffect(() => {
      map.current?.setMapTypeId(mapType)
    }, [mapType])

    const handleChangeMarketPosition = debounce(
      (data: { latitude: string; longitude: string }) => {
        if (data.latitude && data.longitude) {
          const position = new google.maps.LatLng(
            Number(data.latitude),
            Number(data.longitude),
          )
          if (markerDraw.current) {
            markerDraw.current.setPosition(position)
            map.current?.panTo(position)
          }
          setMarkerPosition(position)
        }
      },
      300,
    )

    const renderLabel = (tab: string) => {
      switch (tab) {
        case '0':
          return t('stations')
        case '1':
          return t('flowsensor')
        case '2':
          return t('soilmoisture')
      }
      return ''
    }

    const getFloorShortName = (name: string) => {
      if (!name) return ''

      const clean = name.replace(/[^a-zA-Z0-9\s]/g, '').trim()
      const words = clean.split(/\s+/)
      let shortName = words.map((w) => w[0].toUpperCase()).join('')

      return shortName.slice(0, 3)
    }

    return (
      <Box className={classes.root}>
        {mapOnMap ? (
          <GoogleMap2
            mapType={mapType}
            currentPosition={false}
            mapOutput={mapOutput}
          />
        ) : (
          <GoogleMap
            mapType={mapType}
            currentPosition={false}
            mapOutput={mapOutput}
          />
        )}
        <Box className={classes.topBox}>
          <Box className={classes.topLeft}>
            {singleMode && (
              <Button
                className={classes.btnClose}
                onClick={onClose}
                variant="contained"
              >
                <CloseSmallIcon />
              </Button>
            )}
            <Stack className={classes.selectCustom} style={{ display: 'none' }}>
              <Select
                mapMode
                label={t('formsLabelsSite')}
                isHasMore
                filterFunc={false}
                name="site"
                height="42px"
                bindLabel="name"
                placeholder={t('siteName')}
                value={siteActive}
                className={classes.selectCustom}
                getOptions={getOptions(OPTION_KEY.SITE)}
                onChange={(val: any) => onChangeSite(val)}
              />
              <Select
                mapMode
                isHasMore
                label={t('formsLabelsLocation')}
                filterFunc={false}
                refreshWhenOpen
                name="location"
                height="42px"
                bindLabel="name"
                placeholder={t('formsLabelsLocation')}
                value={locationActive}
                className={classes.selectCustomBottom}
                getOptions={getOptions(OPTION_KEY.LOCATION)}
                onChange={(val: any) => onChangeLocation(val)}
              />
            </Stack>

            {/* <Stack className={classes.selectCustom} style={{ display: 'none' }}>
              <Select
                mapMode
                label={t('Areas&SubAreasArea')}
                isHasMore
                filterFunc={false}
                name="areaParent"
                height="42px"
                bindLabel="name"
                placeholder={t('Areas&SubAreasArea')}
                value={areaActive}
                className={classes.selectCustom}
                getOptions={getOptions(OPTION_KEY.AREA)}
                onChange={(val: any) => onChangeArea(val)}
              />

              <Stack
                sx={{
                  ...(areaActive === undefined && {
                    opacity: 0.5,
                    pointerEvents: 'none',
                    userSelect: 'none',
                  }),
                }}
              >
                <Select
                  mapMode
                  isHasMore
                  label={t('subArea')}
                  filterFunc={false}
                  refreshWhenOpen
                  height="42px"
                  name="subAreaParent"
                  bindLabel="name"
                  placeholder={t('subArea')}
                  className={classes.selectCustomBottom}
                  value={subAreaActive}
                  getOptions={getOptions(OPTION_KEY.SUBAREA)}
                  onChange={(val: any) => onChangeSubArea(val)}
                />
              </Stack>
            </Stack> */}
          </Box>
          <Box className={classes.topCenter}>
            {!station && !singleMode
              ? `${t('SelectA(forGeoTagPanelOnly)')} ${renderLabel(
                  tab as string,
                )} ${t('InTheLeftPanelToPinOnMap(forGeoTagPanelOnly)')}`
              : t('SelectALocationOnTheMapToRetrieveTheCoordinates')}
          </Box>
          <Box className={classes.topRight}>
            <RenderIf isShow={!allowChangePositionCoordinate}>
              <Button
                className={classes.btnShowPosition}
                startIcon={<LocalPinIcon sx={{ color: 'text.main' }} />}
                sx={{
                  backgroundColor: 'background.overrideDefault',
                  color: 'text.main',
                  '&:hover': {
                    backgroundColor: 'background.overrideDefault',
                    color: 'text.main',
                  },
                }}
              >
                {formatPosition(markerPosition)}
              </Button>
            </RenderIf>

            <RenderIf isShow={!!allowChangePositionCoordinate}>
              <CoordinateInput
                value={station ? formatPosition(markerPosition) : ''}
                sx={{
                  backgroundColor: 'background.overrideDefault',
                  color: 'text.main',
                  '&:hover': {
                    backgroundColor: 'background.overrideDefault',
                    color: 'text.main',
                  },
                }}
                onChange={handleChangeMarketPosition}
                readOnly={!station}
              />
            </RenderIf>

            <Button
              disabled={!station && !singleMode}
              className={classes.btnDone}
              onClick={_onDone}
            >
              {t('commonActionsSave')}
            </Button>
          </Box>
        </Box>
        <MapToolBox className={classes.mapToolBoxWrapper}>
          {floors && !!floors.length && (
            <BtnGroupFloor
              variant="text"
              orientation="vertical"
              isRoadMap={mapType === google.maps.MapTypeId.ROADMAP}
            >
              {floors?.map((item) => (
                <Tooltip
                  key={item.id}
                  slotProps={{
                    tooltip: { className: classes.tooltipMenuTxt },
                  }}
                  title={item.name}
                  placement="right"
                  arrow
                >
                  <Button
                    onClick={() => onChangeFloor(item)}
                    variant={
                      item.id === activeFloor?.id ? 'contained' : 'secondary'
                    }
                  >
                    {getFloorShortName(item.name)}
                  </Button>
                </Tooltip>
              ))}
            </BtnGroupFloor>
          )}
        </MapToolBox>

        <BtnGroupZoom isRoadMap={mapType === google.maps.MapTypeId.ROADMAP}>
          <Button onClick={onCurrentLocation}>
            <CurrentLocationIcon />
          </Button>
          <Box className={classes.verticalDivider} />
          <Button onClick={onZoomIn}>
            <MapViewIcon condition={MapViewCondition.zoomIn} />
          </Button>
          <Button onClick={onZoomOut}>
            <MapViewIcon condition={MapViewCondition.zoomOut} />
          </Button>
        </BtnGroupZoom>

        <MapTypeButtonGroup
          orientation="horizontal"
          isRoadMap={mapType === google.maps.MapTypeId.ROADMAP}
        >
          <Button
            onClick={() => onChangeMapType(google.maps.MapTypeId.ROADMAP)}
            variant={
              mapType === google.maps.MapTypeId.ROADMAP ? 'contained' : 'text'
            }
          >
            {t('mapViewNormalMapType')}
          </Button>
          <Button
            onClick={() => onChangeMapType(google.maps.MapTypeId.HYBRID)}
            variant={
              mapType === google.maps.MapTypeId.HYBRID ? 'contained' : 'text'
            }
          >
            {t('mapViewSatelliteMapType')}
          </Button>
        </MapTypeButtonGroup>
      </Box>
    )
  },
)

export default MapPositionStation
